import { DashboardSystemCurrentTimeResponse } from "@/shared/api";
import {
  ExclamationTriangleIcon,
  InfoCircledIcon,
} from "@radix-ui/react-icons";
import {
  Badge,
  Box,
  Button,
  Callout,
  DataList,
  Dialog,
  Flex,
  Heading,
  Progress,
  Separator,
  Text,
} from "@radix-ui/themes";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { calculateProgress } from "../lib/helpers";
import { useCopyToClipboard } from "react-use";
import toast from "react-hot-toast";

const PROGRESS_WINDOW_MINUTES = 30;

const PROGRESS_PREDICTION_KEYS: (keyof DashboardSystemCurrentTimeResponse["result"])[] =
  [
    "latest_track_datetime",
    "change_occlusion_finder_current_datetime",
    "proximity_current_datetime",
    "change_occlusion_interval_current_datetime",
    "change_inference_current_datetime",
  ];

type OverallProgressProps = {
  data: DashboardSystemCurrentTimeResponse["result"];
  orderTime: string | undefined;
  hasJourneysCreated?: boolean;
  finalizedAtTitle?: string;
  isCompleted: boolean;
};

export function OverallProgress({
  data,
  hasJourneysCreated,
  finalizedAtTitle,
  orderTime,
  isCompleted,
}: OverallProgressProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { minDate, endTime, startTime, overallProgress, isDelayed, delay } =
    useMemo(() => {
      const minDate = dayjs(data.system_datetime);
      const endTime = dayjs(orderTime);
      const startTime = endTime.subtract(PROGRESS_WINDOW_MINUTES, "minute");
      const overallProgress = calculateProgress(minDate, startTime, endTime);
      const isDelayed = minDate.isBefore(startTime);
      const delay = dayjs
        .duration(dayjs(minDate).diff(startTime))
        .format("D[d] H[h] m[m] s[s]")
        .replace(/^0d\s|\s?0h\s|\s0m\s|\s0s\s/g, "");

      return {
        minDate,
        endTime,
        startTime,
        overallProgress,
        isDelayed,
        delay,
      };
    }, [data, orderTime]);

  return (
    <Box className="space-y-1" pr="2">
      {isDelayed ? (
        <Callout.Root
          color="red"
          size="1"
          mb="2"
          mr="2"
          className="h-[74px] items-center py-0 w-full"
        >
          <Callout.Icon>
            <ExclamationTriangleIcon width={28} height={28} />
          </Callout.Icon>
          <Callout.Text className="w-full block">
            <Text size="2" as="p" weight="medium">
              Prediction process exceeded its allowed delay.
            </Text>
            <Text size="1" as="p" mb="1">
              Delay: {delay}
            </Text>
            <Flex align="center" gap="2" justify="between" className="w-full">
              <Button color="red" size="1" onClick={() => setIsOpen(true)}>
                Check status
              </Button>
              {hasJourneysCreated === false && (
                <Badge color="red" variant="soft">
                  No journeys
                </Badge>
              )}
            </Flex>
          </Callout.Text>
        </Callout.Root>
      ) : (
        <>
          <Flex justify="start" gap="2" align="center" className="min-h-6">
            <Heading weight="medium" size="2">
              Prediction progress
            </Heading>

            <Button
              variant="soft"
              color="gray"
              size="1"
              onClick={() => setIsOpen(true)}
            >
              Check status
            </Button>

            <div className="grow" />

            {hasJourneysCreated === false && (
              <Badge color="red" variant="soft">
                No journeys
              </Badge>
            )}
          </Flex>
          <OverallProgressShortInfo
            minDate={minDate}
            overallProgress={overallProgress}
            startTime={startTime}
            finalizedAtTitle={finalizedAtTitle}
            endTime={endTime}
            isCompleted={isCompleted}
          />
        </>
      )}

      <Dialog.Root open={isOpen} onOpenChange={setIsOpen}>
        <Dialog.Content>
          <OverallProgressDetails
            minDate={minDate}
            isDelayed={isDelayed}
            startTime={startTime}
            endTime={endTime}
            overallProgress={overallProgress}
            data={data}
            delay={delay}
            finalizedAtTitle={finalizedAtTitle}
            isCompleted={isCompleted}
          />
        </Dialog.Content>
      </Dialog.Root>
    </Box>
  );
}

type OverallProgressShortInfoProps = {
  overallProgress: number;
  startTime: dayjs.Dayjs;
  endTime: dayjs.Dayjs;
  minDate: dayjs.Dayjs;
  finalizedAtTitle?: string;
  isCompleted: boolean;
};

function OverallProgressShortInfo({
  overallProgress,
  endTime,
  minDate,
  finalizedAtTitle,
  isCompleted,
}: OverallProgressShortInfoProps) {
  const [_, copy] = useCopyToClipboard();

  const onCopy = (text: string) => () => {
    copy(text);
    toast.success("Copied to clipboard");
  };

  return (
    <Box className="space-y-1">
      <Progress value={Math.min(overallProgress, 100)} />

      <Flex justify="between" className="relative">
        <Box>
          <Text size="1" as="p" color="gray" mb="1">
            System time:
          </Text>
          <Button
            highContrast
            variant="ghost"
            color="gray"
            size="1"
            onClick={onCopy(minDate.toISOString())}
          >
            {minDate.toISOString()}
          </Button>
        </Box>
        {!isCompleted ? (
          <Box>
            <Text align="right" size="1" as="p" color="gray" mb="1">
              {finalizedAtTitle ?? "Finalized at"}:
            </Text>
            <Button
              highContrast
              variant="ghost"
              color="gray"
              size="1"
              onClick={onCopy(endTime.toISOString())}
            >
              {endTime.toISOString()}
            </Button>
          </Box>
        ) : (
          <Badge
            color="green"
            variant="soft"
            className="absolute right-0 top-2"
          >
            Completed
          </Badge>
        )}
      </Flex>
    </Box>
  );
}

type OverallProgressDetailsProps = {
  startTime: dayjs.Dayjs;
  minDate: dayjs.Dayjs;
  endTime: dayjs.Dayjs;
  overallProgress: number;
  data: DashboardSystemCurrentTimeResponse["result"];
  isDelayed: boolean;
  delay: string;
  finalizedAtTitle?: string;
  isCompleted: boolean;
};

function OverallProgressDetails({
  minDate,
  endTime,
  overallProgress,
  data,
  isDelayed,
  delay,
  startTime,
  finalizedAtTitle,
  isCompleted,
}: OverallProgressDetailsProps) {
  const getItemProgress = (
    key: keyof DashboardSystemCurrentTimeResponse["result"]
  ) => {
    if (isDelayed) return 0;

    const progress = calculateProgress(
      dayjs(data[key] as string),
      startTime,
      endTime
    );

    return Math.min(progress, 100);
  };

  return (
    <>
      <Dialog.Title>Overall progress</Dialog.Title>
      {isDelayed ? (
        <DataList.Root size="1" className="gap-1">
          <DataList.Item>
            <DataList.Label>System time</DataList.Label>
            <DataList.Value>{minDate.toISOString()}</DataList.Value>
          </DataList.Item>
          <DataList.Item>
            <DataList.Label>
              {finalizedAtTitle ?? "Finalized at"}
            </DataList.Label>
            <DataList.Value>{endTime.toISOString()}</DataList.Value>
          </DataList.Item>
          <DataList.Item>
            <DataList.Label>Difference</DataList.Label>
            <DataList.Value>{delay}</DataList.Value>
          </DataList.Item>
        </DataList.Root>
      ) : (
        <OverallProgressShortInfo
          minDate={minDate}
          overallProgress={overallProgress}
          startTime={startTime}
          endTime={endTime}
          finalizedAtTitle={finalizedAtTitle}
          isCompleted={isCompleted}
        />
      )}
      <Separator size="4" my="4" />

      <Box className="space-y-3">
        {PROGRESS_PREDICTION_KEYS.map((key) => (
          <ProgressItem
            key={key}
            title={key}
            progress={getItemProgress(key)}
            systemTime={data[key] as string}
          />
        ))}
      </Box>

      <Callout.Root color="gray" size="1" mt="4">
        <Callout.Icon>
          <InfoCircledIcon />
        </Callout.Icon>
        <Callout.Text>
          <Text size="1" as="p" color="gray">
            Note: <br /> Progress is calculated based on the final{" "}
            {PROGRESS_WINDOW_MINUTES} minutes before the “
            {finalizedAtTitle ?? "Finalized at"}” time.
          </Text>
        </Callout.Text>
      </Callout.Root>
    </>
  );
}

type ProgressItemProps = {
  title: string;
  progress: number;
  systemTime: string;
};

function ProgressItem({ title, progress, systemTime }: ProgressItemProps) {
  const [_, copy] = useCopyToClipboard();

  const onCopy = () => {
    copy(systemTime);
    toast.success("Copied to clipboard");
  };

  return (
    <Flex direction="column" gap="2" align="start">
      <Text
        size="1"
        as="p"
        color="gray"
        weight="bold"
        highContrast
        className="capitalize"
      >
        {title.replaceAll("_", " ")}
      </Text>
      <Box className="w-full">
        <Progress value={progress} />
      </Box>
      <Text size="1" as="p" color="gray">
        System time:
        <Button
          ml="2"
          highContrast
          variant="ghost"
          color="gray"
          size="1"
          onClick={onCopy}
          className="px-1"
        >
          {dayjs(systemTime).toISOString()}
        </Button>
      </Text>
    </Flex>
  );
}
